<script lang="ts" setup>
/**
 * The circuit at the head of the menu, only where a plant has more than one.
 * Tapped, it lists the basins, each with a dot that turns red while its
 * collective alarm stands.
 */
import { onUnmounted, ref, watch } from 'vue'

import { useCircuit } from '@/composables/useCircuit'
import { isOn } from '@/composables/useMeasurement'
import { getDeviceIdentifier } from '@/composables/useMqttConnection'
import type { ISystem } from '@/interfaces/gui'
import type { IDatapoint } from '@/interfaces/IDatapoint'
import { useMqttStore } from '@/stores/mqtt'
import { translate } from '@/utils'

const emit = defineEmits<{
  chosen: []
}>()

const { circuits, currentCircuit, isMultiCircuit, switchCircuit } = useCircuit()
const mqttStore = useMqttStore()

const open = ref(false)
const faults = ref<Record<string, boolean>>({})

let unsubscribers: Array<() => void> = []

function listen(all: ReadonlyArray<ISystem>): void {
  unsubscribers.forEach((unsubscribe) => unsubscribe())
  unsubscribers = all
    .filter((circuit) => circuit.error)
    .map((circuit) =>
      mqttStore.subscribe(`${getDeviceIdentifier()}/datapoints/${circuit.error}`, (value) => {
        faults.value = { ...faults.value, [circuit.name]: isOn(value as IDatapoint | null) }
      }),
    )
}

watch(circuits, (all) => listen(all), { immediate: true })
onUnmounted(() => unsubscribers.forEach((unsubscribe) => unsubscribe()))

function choose(circuit: ISystem): void {
  open.value = false
  switchCircuit(circuit)
  emit('chosen')
}
</script>

<template>
  <div v-if="isMultiCircuit" class="mb-2" data-component-name="CircuitPicker">
    <button
      class="circuit-current flex min-h-11 w-full items-center gap-3 rounded-md bg-slate-100 p-3 text-left transition-colors hover:bg-slate-200"
      type="button"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="flex min-w-0 flex-1 flex-col">
        <span class="text-xs uppercase tracking-wide text-slate-500">{{
          translate('#circuit')
        }}</span>
        <span class="truncate text-base font-medium text-slate-800">{{
          translate(currentCircuit?.label || '')
        }}</span>
      </span>
      <svg
        class="h-3 w-3 shrink-0 text-slate-500 transition-transform duration-200"
        :class="open ? '-rotate-90' : 'rotate-90'"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="4"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <ul v-if="open" class="circuit-list mt-1 list-none p-0">
      <li v-for="circuit in circuits" :key="circuit.name">
        <button
          class="circuit-option flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-left hover:bg-slate-100"
          :class="{ 'bg-slate-50 font-medium': circuit.name === currentCircuit?.name }"
          type="button"
          :aria-current="circuit.name === currentCircuit?.name ? 'true' : undefined"
          @click="choose(circuit)"
        >
          <span
            class="h-2.5 w-2.5 shrink-0 rounded-full"
            :class="faults[circuit.name] ? 'bg-red-500' : 'bg-green-500'"
            :data-fault="faults[circuit.name] ? 'true' : 'false'"
            aria-hidden="true"
          />
          <span class="flex-1 truncate text-base text-slate-800">{{
            translate(circuit.label)
          }}</span>
          <span v-if="faults[circuit.name]" class="sr-only">{{
            translate('#common_alarm_message')
          }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>
